import { useState, useEffect } from "react";
import SectionLabel from "@/components/SectionLabel";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Cake, HandHeart, Sparkles, Flower } from "lucide-react";

const TYPES = [
  {
    id: "celebration",
    label: "Celebration",
    sub: "Birthday · Anniversary · Special Occasion",
    icon: Cake,
    color: "text-gold",
    needsOccasion: true,
  },
  {
    id: "petition",
    label: "Petition",
    sub: "For a particular need",
    icon: HandHeart,
    color: "text-terracotta",
  },
  {
    id: "thanksgiving",
    label: "Thanksgiving",
    sub: "For blessings received",
    icon: Sparkles,
    color: "text-sage-dark",
  },
  {
    id: "repose",
    label: "Repose of the Soul",
    sub: "For a deceased loved one",
    icon: Flower,
    color: "text-cocoa-700",
  },
];

const OCCASIONS = ["Birthday", "Wedding Anniversary", "Baptism Anniversary", "Other Special Occasion"];

const blank = {
  type: "",
  occasion: "",
  intention: "",
  requested_by: "",
  contact: "",
  email: "",
  preferred_date: "",
};

export default function MassIntentions() {
  const [form, setForm] = useState(blank);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [publicList, setPublicList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const loadPublic = async () => {
    setLoadingList(true);
    try {
      const res = await api.get("/intentions/public");
      setPublicList(res.data || []);
    } catch {
      // soft-fail; listing is optional
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadPublic();
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const selectedType = TYPES.find((t) => t.id === form.type);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.type) return toast.error("Please select an intention type.");
    if (!form.intention.trim()) return toast.error("Please write your intention.");
    if (!form.requested_by.trim()) return toast.error("Please tell us who is requesting.");

    setSubmitting(true);
    try {
      const payload = {
        type: form.type,
        occasion: selectedType?.needsOccasion ? form.occasion : "",
        intention: form.intention,
        requested_by: form.requested_by,
        contact: form.contact || "",
        email: form.email || null,
        preferred_date: form.preferred_date || null,
      };
      await api.post("/intentions", payload);
      setSuccess(
        "Thank you. Your intention has been received and will be remembered in the Holy Mass."
      );
      toast.success("Intention received — it will be lifted in prayer.");
      setForm(blank);
      loadPublic();
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="intentions" className="py-24 md:py-36 bg-parchment-100 relative grain">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_85%,rgba(143,151,121,0.08),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel
          kicker="Pray with us"
          title="Mass Intentions"
          intro="Entrust your prayers to the parish community. Your intentions will be lifted in the Holy Mass — for celebrations, petitions, thanksgiving, and the repose of loved ones."
        />

        <div className="mt-16 grid lg:grid-cols-12 gap-10">
          {/* Type cards / sidebar */}
          <aside className="lg:col-span-4 space-y-3">
            {TYPES.map((t) => {
              const Icon = t.icon;
              const active = form.type === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`intention-type-${t.id}`}
                  onClick={() => setForm((f) => ({ ...f, type: t.id, occasion: "" }))}
                  className={`w-full text-left border bg-parchment-50 p-5 transition-all duration-300 flex gap-4 items-start group ${
                    active
                      ? "border-terracotta bg-terracotta/[0.06] -translate-x-1"
                      : "border-parchment-400 hover:border-cocoa-900/40"
                  }`}
                >
                  <Icon className={t.color} size={26} strokeWidth={1.3} />
                  <div className="flex-1">
                    <div className="font-serif text-xl text-cocoa-900 leading-tight">
                      {t.label}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-sage-dark mt-1 font-sans">
                      {t.sub}
                    </div>
                  </div>
                  <span
                    className={`text-terracotta text-xl transition-opacity ${
                      active ? "opacity-100" : "opacity-0 group-hover:opacity-40"
                    }`}
                  >
                    ✦
                  </span>
                </button>
              );
            })}
          </aside>

          {/* Form */}
          <div className="lg:col-span-8 bg-parchment-50 border border-parchment-400 p-8 md:p-12">
            {!form.type ? (
              <div className="flex flex-col items-center justify-center text-center py-24">
                <div className="font-serif text-3xl text-cocoa-900 italic mb-3">
                  Choose an intention type
                </div>
                <p className="font-sans text-cocoa-700 max-w-md">
                  Select one of the categories on the left to begin offering your prayer.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-7">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-terracotta font-medium mb-2 font-sans">
                    {selectedType.label}
                  </div>
                  <h3 className="font-serif text-3xl text-cocoa-900 leading-tight">
                    Tell us about your intention.
                  </h3>
                </div>

                {selectedType.needsOccasion && (
                  <div>
                    <Label>Occasion</Label>
                    <select
                      data-testid="intention-occasion"
                      value={form.occasion}
                      onChange={update("occasion")}
                      className="field"
                    >
                      <option value="">Select an occasion</option>
                      {OCCASIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <Label>
                    {selectedType.id === "repose"
                      ? "Name of the deceased & a short prayer"
                      : selectedType.id === "celebration"
                      ? "For whom (celebrant) & a short prayer"
                      : "Your intention"}
                  </Label>
                  <textarea
                    data-testid="intention-text"
                    value={form.intention}
                    onChange={update("intention")}
                    rows={4}
                    placeholder={
                      selectedType.id === "thanksgiving"
                        ? "e.g. In thanksgiving for safe delivery of our newborn..."
                        : selectedType.id === "petition"
                        ? "e.g. For the healing of my mother..."
                        : selectedType.id === "repose"
                        ? "e.g. For the eternal repose of the soul of Juan dela Cruz..."
                        : "e.g. For the 70th birthday of Lola Maria, in thanksgiving for her life..."
                    }
                    className="field min-h-[120px] resize-y"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Requested by</Label>
                    <input
                      data-testid="intention-requested-by"
                      value={form.requested_by}
                      onChange={update("requested_by")}
                      placeholder="Your name or family name"
                      className="field"
                    />
                  </div>
                  <div>
                    <Label>Preferred Mass date (optional)</Label>
                    <input
                      type="date"
                      data-testid="intention-preferred-date"
                      value={form.preferred_date}
                      onChange={update("preferred_date")}
                      className="field"
                    />
                  </div>
                  <div>
                    <Label>Contact number (optional)</Label>
                    <input
                      data-testid="intention-contact"
                      value={form.contact}
                      onChange={update("contact")}
                      placeholder="09XX XXX XXXX"
                      className="field"
                    />
                  </div>
                  <div>
                    <Label>Email (optional)</Label>
                    <input
                      type="email"
                      data-testid="intention-email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="name@example.com"
                      className="field"
                    />
                  </div>
                </div>

                <div className="pt-2 flex flex-wrap items-center gap-5">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="intention-submit"
                    className="inline-flex items-center gap-3 bg-cocoa-900 text-parchment-100 px-8 py-4 font-sans text-sm tracking-wide hover:bg-terracotta transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Offering…" : "Offer this Intention"}
                    <span>→</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setForm(blank);
                      setSuccess("");
                    }}
                    className="text-sm text-cocoa-700 hover:text-terracotta font-sans"
                  >
                    Clear
                  </button>
                </div>

                {success && (
                  <p
                    data-testid="intention-success"
                    className="mt-2 font-serif italic text-sage-dark text-lg"
                  >
                    ✦ {success}
                  </p>
                )}

                <p className="text-[11px] uppercase tracking-[0.22em] text-sage-dark italic mt-4">
                  ✦ Mass stipends and final Mass scheduling are coordinated by the parish office.
                </p>
              </form>
            )}
          </div>
        </div>

        {/* Public listing of recent intentions */}
        <PublicIntentionsBoard items={publicList} loading={loadingList} />


        {/* Inline field styling */}
        <style>{`
          .field {
            width: 100%;
            background: transparent;
            border: 0;
            border-bottom: 1px solid rgba(122,92,79,0.4);
            padding: 10px 2px;
            font-family: 'Figtree', system-ui, sans-serif;
            color: #3E2723;
            outline: none;
            transition: border-color 0.2s;
          }
          .field:focus { border-bottom-color: #C25953; }
          .field::placeholder { color: rgba(62,39,35,0.3); font-style: italic; }
        `}</style>
      </div>
    </section>
  );
}

function Label({ children }) {
  return (
    <label className="text-[10px] uppercase tracking-[0.28em] text-sage-dark font-sans block mb-1">
      {children}
    </label>
  );
}

const typeMeta = {
  celebration: { label: "Celebration", color: "text-gold", border: "border-gold/40" },
  petition: { label: "Petition", color: "text-terracotta", border: "border-terracotta/40" },
  thanksgiving: { label: "Thanksgiving", color: "text-sage-dark", border: "border-sage/40" },
  repose: { label: "Repose of the Soul", color: "text-cocoa-700", border: "border-cocoa-900/30" },
};

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "numeric" });
}

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

function PublicIntentionsBoard({ items, loading }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? items : items.slice(0, 6);

  return (
    <div className="mt-24">
      <div className="text-center mb-12">
        <div className="flex items-center gap-3 mb-4 justify-center">
          <span className="h-px w-10 bg-sage" />
          <span className="text-[11px] uppercase tracking-[0.3em] text-sage-dark font-sans">
            Walking with you in prayer
          </span>
          <span className="h-px w-10 bg-sage" />
        </div>
        <h3 className="font-serif text-3xl md:text-4xl text-cocoa-900 leading-tight">
          Recent Mass Intentions
        </h3>
        <p className="mt-4 font-sans text-cocoa-700 text-sm max-w-2xl mx-auto font-light leading-relaxed">
          The intentions of our parishioners and visitors, as they are offered. Names of contributors are
          shared with care; private contact details are never displayed.
        </p>
      </div>

      {loading ? (
        <div className="text-center text-cocoa-700 font-sans py-10">Loading intentions…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-parchment-400 p-12 text-center text-cocoa-700 font-sans">
          ✦ No intentions yet. Be the first to offer a prayer above.
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {visible.map((i) => {
              const meta = typeMeta[i.type] || typeMeta.petition;
              const massDate = fmtDate(i.scheduled_date) || fmtDate(i.preferred_date);
              return (
                <article
                  key={i.id}
                  data-testid="public-intention-card"
                  className={`bg-parchment-50 border-l-2 ${meta.border} border-y border-r border-parchment-400 p-6 flex flex-col`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-[10px] uppercase tracking-[0.26em] font-sans ${meta.color}`}>
                      {meta.label}
                      {i.occasion ? <span className="text-sage-dark"> · {i.occasion}</span> : null}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-sage-dark font-sans">
                      {fmtRelative(i.created_at)}
                    </span>
                  </div>

                  <p className="font-serif italic text-cocoa-900 leading-relaxed text-[15px] flex-1">
                    &ldquo;{i.intention}&rdquo;
                  </p>

                  <div className="mt-4 pt-3 border-t border-parchment-300 flex items-center justify-between gap-3 text-[11px] font-sans">
                    <span className="uppercase tracking-[0.2em] text-cocoa-700">
                      — {i.requested_by}
                    </span>
                    {massDate ? (
                      <span
                        className={`uppercase tracking-[0.18em] ${
                          i.scheduled_date ? "text-terracotta" : "text-sage-dark"
                        }`}
                        title={i.scheduled_date ? "Scheduled by parish office" : "Requested date"}
                      >
                        {i.scheduled_date ? "✦ Mass · " : "For Mass · "}
                        {massDate}
                      </span>
                    ) : (
                      <span className="uppercase tracking-[0.18em] text-cocoa-700/60">
                        Coming Mass
                      </span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {items.length > 6 && (
            <div className="mt-8 text-center">
              <button
                onClick={() => setShowAll((v) => !v)}
                data-testid="intentions-toggle-all"
                className="inline-flex items-center gap-3 border border-cocoa-900/40 bg-parchment-50 text-cocoa-900 px-7 py-3 font-sans text-sm tracking-wide hover:bg-cocoa-900 hover:text-parchment-100 transition-colors"
              >
                {showAll ? "Show fewer" : `Show all ${items.length} intentions`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
